import Rollbar, { Configuration, LogArgument, Dictionary } from 'rollbar';
import global from './global';

interface ExceptionLoggerOptions extends Configuration {
  meta?: object;
  environment?: string;
}

const isServer = typeof window === 'undefined';

/**
 * Default checkIgnore method which is used internally to prevent exceptions from test environments going to rollbar
 * @returns {boolean} - Returns true for a rollbar exception to be ignored and false for it not to be ignored
 */
export const defaultCheckIgnore = (
  _isUncaught: boolean,
  _args: LogArgument[],
  payload: Dictionary,
) => {
  return payload && payload.environment === 'test';
};

const makeOptions = ({
  accessToken = isServer
    ? process.env.EXCEPTION_LOGGER_SERVER_TOKEN
    : global.circleci?.config?.exceptionLoggerClientToken,
  meta = {},
  captureUncaught = true,
  captureUnhandledRejections = true,
  captureIp = 'anonymize',
  environment = process.env.NODE_ENV,
  verbose = !isServer,
  ...args
}: ExceptionLoggerOptions): Rollbar.Configuration => {
  const buildId = process.env.BUILD_ID;
  const enableSourceMaps = buildId ? true : false;
  const { checkIgnore: optionalCheckIgnore } = args;

  return {
    captureUncaught,
    captureUnhandledRejections,
    enabled: !!accessToken,
    accessToken,
    payload: {
      ...meta,
      environment,
      client: {
        javascript: {
          code_version: buildId,
          source_map_enabled: enableSourceMaps,
        },
      },
      server: {
        root: 'webpack://_N_E/./src/',
      },
    },
    verbose,
    captureIp,
    scrubFields: ['circle-token', 'CSRFToken'],
    scrubTelemetryInputs: true,
    ...args,
    checkIgnore: (isUncaught, args, payload) => {
      if (!defaultCheckIgnore(isUncaught, args, payload)) {
        // environment is not test
        if (optionalCheckIgnore) {
          return optionalCheckIgnore(isUncaught, args, payload);
        }
        return false;
      }
      return true;
    },
    hostSafeList: ['circleci.com', 'optimizely.com'],
  };
};

export default (args?: ExceptionLoggerOptions): Rollbar =>
  Rollbar.init(makeOptions(args || {}));
